import type { CrmFile, Customer, CustomerModule, Followup, Meeting, Offer, Order, Renewal, SupportRequest, Trial, WorkRecord } from '../types'
import { CustomerFormModal } from './CustomerFormModal'
import { CollectionForm, InvoiceForm, ReceiptForm } from './forms/FinanceForms'
import { FileForm, UnlinkFileModal } from './forms/FileForms'
import { MeetingForm } from './forms/MeetingForm'
import { OfferForm } from './forms/OfferForm'
import { EmailForm, ModuleForm, RequestForm, WorkForm } from './forms/RecordForms'
import { ApproveOrderForm, RejectOrderForm } from './forms/OrderForms'
import { FollowupForm, RenewalForm } from './forms/SalesForms'
import { ConvertTrialForm, ExtendTrialForm, StartTrialForm } from './forms/TrialForms'

export type CrmDialog =
  | { kind: 'customer' }
  | { kind: 'meeting'; meeting: Meeting | null; plan?: boolean }
  | { kind: 'offer'; offer: Offer | null }
  | { kind: 'module'; module: CustomerModule | null }
  | { kind: 'work'; work: WorkRecord | null }
  | { kind: 'collection' }
  | { kind: 'invoice' }
  | { kind: 'receipt'; collectionId?: string }
  | { kind: 'request'; request: SupportRequest | null }
  | { kind: 'email' }
  | { kind: 'file'; file: CrmFile | null }
  | { kind: 'unlink'; file: CrmFile }
  | { kind: 'approveOrder'; order: Order }
  | { kind: 'rejectOrder'; order: Order }
  | { kind: 'renewal'; renewal: Renewal | null }
  | { kind: 'followup'; followup: Followup | null }
  | { kind: 'trial' }
  | { kind: 'extendTrial'; trial: Trial }
  | { kind: 'convertTrial'; trial: Trial }

/** Seçili modalı, ilgili müşteri bağlamında açar. */
export function CrmDialogHost({ dialog, customer, onClose, onMeetingSaved }: { dialog: CrmDialog | null; customer: Customer; onClose: () => void; onMeetingSaved?: (id: string) => void }) {
  if (!dialog) return null
  const id = customer.id
  switch (dialog.kind) {
    case 'customer':
      return <CustomerFormModal customer={customer} onDone={onClose} />
    case 'meeting':
      return (
        <MeetingForm
          customerId={id}
          meeting={dialog.meeting}
          plan={dialog.plan}
          onDone={(saved) => {
            onClose()
            if (saved) onMeetingSaved?.(saved)
          }}
        />
      )
    case 'offer':
      return <OfferForm customerId={id} offer={dialog.offer} onDone={onClose} />
    case 'module':
      return <ModuleForm customerId={id} module={dialog.module} onDone={onClose} />
    case 'work':
      return <WorkForm customerId={id} work={dialog.work} onDone={onClose} />
    case 'collection':
      return <CollectionForm customer={customer} onDone={onClose} />
    case 'invoice':
      return <InvoiceForm customer={customer} onDone={onClose} />
    case 'receipt':
      return <ReceiptForm customer={customer} collectionId={dialog.collectionId} onDone={onClose} />
    case 'request':
      return <RequestForm customerId={id} request={dialog.request} onDone={onClose} />
    case 'email':
      return <EmailForm customerId={id} to={customer.email} onDone={onClose} />
    case 'file':
      return <FileForm customer={customer} file={dialog.file} onDone={onClose} />
    case 'approveOrder':
      return <ApproveOrderForm order={dialog.order} onDone={onClose} />
    case 'rejectOrder':
      return <RejectOrderForm order={dialog.order} onDone={onClose} />
    case 'renewal':
      return <RenewalForm customerId={id} renewal={dialog.renewal} onDone={onClose} />
    case 'followup':
      return <FollowupForm customerId={id} followup={dialog.followup} onDone={onClose} />
    case 'trial':
      return <StartTrialForm customerId={id} onDone={onClose} />
    case 'extendTrial':
      return <ExtendTrialForm trial={dialog.trial} onDone={onClose} />
    case 'convertTrial':
      return <ConvertTrialForm trial={dialog.trial} onDone={onClose} />
    case 'unlink':
      return <UnlinkFileModal customer={customer} file={dialog.file} onDone={onClose} />
  }
}
